import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Eye, MapPin, ArrowRight, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { fmtLongDate } from '../utils/dateUtils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog?limit=20`);
      setPosts(response.data);
    } catch {
      // Silently handled - non-critical analytics fetch. Errors are logged server-side.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Blog | North Texas Welding and Fabrication';
    fetchPosts();
  }, [fetchPosts]);

  const formatDate = (dateString) => {
    return fmtLongDate(dateString);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <meta name="description" content="Expert insights on welding, fencing, and fabrication from North Texas Welding and Fabrication. Tips, guides, and industry news for Texas property owners." />
        <link rel="canonical" href="https://ntxpipefence.com/blog" />
        <meta property="og:title" content="Blog | North Texas Welding and Fabrication" />
        <meta property="og:description" content="Expert insights on welding, fencing, and fabrication from North Texas Welding and Fabrication." />
        <meta property="og:url" content="https://ntxpipefence.com/blog" />
      </Helmet>
      {/* Header */}
      <header className="bg-black border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                <span className="text-red-600">North Texas Welding</span> Blog
              </h1>
              <p className="text-zinc-400">Expert insights on welding, fencing, and fabrication</p>
            </div>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black"
            >
              <Home className="mr-2" size={20} />
              Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-zinc-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Welding & Fencing Insights
          </h2>
          <p className="text-zinc-300 text-lg max-w-3xl mx-auto">
            Stay informed with expert tips, guides, and industry insights for landowners 
            across Springtown, Weatherford, Parker County, and North Texas.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
              <p className="text-zinc-600">Loading articles...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-zinc-600 text-lg">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Card 
                  key={post.id} 
                  className="border-zinc-200 hover:border-red-600 transition-all hover:shadow-xl cursor-pointer group"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-zinc-500 mb-4">
                      <div className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {formatDate(post.generated_at)}
                      </div>
                      <div className="flex items-center">
                        <Eye size={14} className="mr-1" />
                        {post.views}
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-zinc-900 mb-3 group-hover:text-red-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>

                    <p className="text-zinc-600 mb-4 line-clamp-3">
                      {post.meta_description}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-zinc-500">
                        <MapPin size={14} className="mr-1" />
                        {post.service_area}
                      </div>
                      
                      <Button 
                        variant="ghost" 
                        className="text-red-600 hover:text-red-700 p-0"
                      >
                        Read More <ArrowRight size={16} className="ml-1" />
                      </Button>
                    </div>

                    <div className="mt-4 pt-4 border-t border-zinc-200">
                      <span className="text-xs text-zinc-500">
                        {post.word_count} words • {Math.ceil(post.word_count / 200)} min read
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-zinc-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-zinc-300 text-lg mb-8">
            Get expert welding and fencing services from North Texas Welding and Fabrication
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/#contact')}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white font-semibold"
            >
              Get Free Estimate
            </Button>
            <Button 
              as="a"
              href="tel:8177235006"
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black font-semibold"
            >
              Call 817-723-5006
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-8 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-zinc-400 text-sm">
            © {new Date().getFullYear()} North Texas Welding and Fabrication, LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
